import { useRef, useState, useEffect } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '../../LanguageContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const DEFAULT_SERVICES = [
  { id: 'svc1', title: '2D Çalışmalar', subtitle: 'Görsel Tasarım', color: '#f7951d',
    items: ['Albüm & Şarkı Kapakları', 'Tek Kare Renderlar', 'Poster Tasarımı', 'Sosyal Medya Görselleri'] },
  { id: 'svc2', title: '3D Çalışmalar', subtitle: 'Dijital Sanat', color: '#2faee0',
    items: ['3D Video', 'DJ Visuals', 'VFX', 'Motion Graphics'] },
  { id: 'svc3', title: 'Branding', subtitle: 'Kimlik Tasarımı', color: '#bc13fe',
    items: ['Logo Tasarımı', 'Kurumsal Kimlik', 'Marka Rehberi', 'Ambalaj Tasarımı'] },
];

const DEFAULT_HEADING = { label: 'Hizmetler', title: 'Ne Yapıyoruz', subtitle: 'Yaratıcı stüdyomuzun sunduğu hizmetler' };

const ServiceFlag = ({ service, index, isInView }) => {
  const [hovered, setHovered] = useState(false);
  const { t } = useLanguage();

  // Try to get translated title and subtitle
  const translatedTitle = t('services', `${service.id}_title`);
  const translatedSubtitle = t('services', `${service.id}_subtitle`);
  const translatedItemsRaw = t('services', `${service.id}_items`);

  const displayTitle = (translatedTitle && translatedTitle !== `${service.id}_title`) ? translatedTitle : service.title;
  const displaySubtitle = (translatedSubtitle && translatedSubtitle !== `${service.id}_subtitle`) ? translatedSubtitle : service.subtitle;
  
  // Handle items: split if it's a comma-separated string from translations
  let displayItems = service.items || [];
  if (translatedItemsRaw && translatedItemsRaw !== `${service.id}_items` && typeof translatedItemsRaw === 'string') {
    displayItems = translatedItemsRaw.split(',').map(i => i.trim()).filter(Boolean);
  }

  return (
    <motion.div
      className="relative flex-1 min-w-[260px] cursor-default"
      initial={{ opacity: 0, y: 60, scale: 0.94 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 60, scale: 0.94 }}
      transition={{ duration: 0.8, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Glow */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: `radial-gradient(ellipse at 50% -20%, ${service.color}35 0%, transparent 65%)`,
        opacity: hovered ? 1 : 0, transition: 'opacity 0.5s ease',
        filter: 'blur(20px)', transform: 'translateY(-10px)',
      }} />

      <motion.div
        className="relative overflow-hidden h-full"
        animate={{ y: hovered ? -8 : 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        style={{
          border: `1px solid ${hovered ? service.color + '55' : 'rgba(255,255,255,0.07)'}`,
          background: hovered ? `linear-gradient(155deg, ${service.color}10 0%, rgba(3,3,3,0) 55%)` : 'rgba(255,255,255,0.02)',
          transition: 'border-color 0.4s ease, background 0.4s ease',
        }}
      >
        {/* Bayrak çıtası */}
        <motion.div
          style={{ height: '3px', background: service.color, transformOrigin: 'left' }}
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ duration: 0.7, delay: index * 0.15 + 0.25, ease: [0.16, 1, 0.3, 1] }}
        />

        <div className="p-8 md:p-10">
          <motion.span className="font-mono text-[11px] tracking-[0.45em] uppercase block mb-6"
            style={{ color: service.color + 'bb' }}
            initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ delay: index * 0.15 + 0.2 }}
          >
            {String(index + 1).padStart(2, '0')} ──
          </motion.span>

          <h3 className="font-mono text-2xl md:text-3xl font-bold tracking-tight uppercase leading-tight"
            style={{ color: hovered ? service.color : 'rgba(255,255,255,0.92)', transition: 'color 0.4s ease' }}>
            {displayTitle}
          </h3>

          {displaySubtitle && (
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] mt-2"
              style={{ color: 'rgba(255,255,255,0.22)' }}>
              {displaySubtitle}
            </p>
          )}

          <div style={{
            height: '1px', margin: '1.5rem 0',
            background: `linear-gradient(to right, ${service.color}70, transparent)`,
            transform: `scaleX(${hovered ? 1 : 0.25})`, transformOrigin: 'left',
            transition: 'transform 0.5s ease, opacity 0.5s ease',
            opacity: hovered ? 1 : 0.4,
          }} />

          <ul className="space-y-[10px]">
            {displayItems.map((item, i) => (
              <motion.li key={i} className="flex items-center gap-3 font-mono text-sm"
                initial={{ opacity: 0, x: -10 }}
                animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                transition={{ duration: 0.4, delay: index * 0.15 + 0.35 + i * 0.07 }}
              >
                <span className="flex-shrink-0 rounded-full" style={{
                  width: '5px', height: '5px', background: service.color,
                  opacity: hovered ? 1 : 0.55,
                  transform: hovered ? 'scale(1.4)' : 'scale(1)',
                  transition: `opacity 0.3s ease ${i * 0.04}s, transform 0.3s ease ${i * 0.04}s`,
                }} />
                <span style={{
                  color: hovered ? 'rgba(255,255,255,0.82)' : 'rgba(255,255,255,0.45)',
                  transition: `color 0.3s ease ${i * 0.03}s`,
                }}>
                  {item}
                </span>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Sağ kenar */}
        <div className="absolute right-0 top-0 bottom-0" style={{
          width: '2px',
          background: `linear-gradient(to bottom, transparent, ${service.color}70, transparent)`,
          opacity: hovered ? 1 : 0, transition: 'opacity 0.4s ease',
        }} />

        {/* Alt köşe */}
        <div className="absolute bottom-0 right-0 pointer-events-none" style={{
          width: '60px', height: '60px',
          background: `linear-gradient(225deg, ${service.color}18 0%, transparent 60%)`,
          opacity: hovered ? 1 : 0, transition: 'opacity 0.4s ease',
        }} />
      </motion.div>
    </motion.div>
  );
};

export const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [heading, setHeading] = useState(DEFAULT_HEADING);
  const { t } = useLanguage();

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const bgY    = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);
  const titleY = useTransform(scrollYProgress, [0, 0.5], ['0%', '-6%']);

  useEffect(() => {
    fetch(`${API}/services-data`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data?.services) && data.services.length > 0) setServices(data.services);
        if (data?.heading) setHeading({ ...DEFAULT_HEADING, ...data.heading });
      })
      .catch(() => {});
  }, []);

  // Use translations for heading labels
  const hLabel = t('services', 'label', heading.label);
  const hTitle = t('services', 'title', heading.title);
  const hSubtitle = t('services', 'subtitle', heading.subtitle);

  return (
    <section id="services" ref={ref}
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-24 overflow-hidden"
      style={{ background: '#030303' }}
    >
      <motion.div className="absolute inset-0 pointer-events-none" style={{ y: bgY }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse 90% 40% at 50% 0%, rgba(255,255,255,0.025) 0%, transparent 60%)',
        }} />
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: '120px',
        }} />
      </motion.div>

      <div className="max-w-screen-2xl mx-auto relative">
        <motion.div className="mb-16 md:mb-24" style={{ y: titleY }}>
          <motion.span
            className="inline-block font-mono text-[10px] uppercase tracking-[0.4em] px-4 py-2 mb-8"
            style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' }}
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.6 }}
          >
            {hLabel}
          </motion.span>

          <div className="overflow-hidden mb-2">
            <motion.h2
              className="font-mono text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight uppercase"
              style={{ color: 'rgba(255,255,255,0.95)' }}
              initial={{ y: '100%' }}
              animate={isInView ? { y: 0 } : { y: '100%' }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              {hTitle}
            </motion.h2>
          </div>

          {(hSubtitle) && (
            <motion.p className="font-mono text-sm mt-4 max-w-xl"
              style={{ color: 'rgba(255,255,255,0.32)' }}
              initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ delay: 0.35 }}
            >
              {hSubtitle}
            </motion.p>
          )}
        </motion.div>

        <div className="flex flex-col md:flex-row gap-4 md:gap-5 lg:gap-6 items-stretch">
          {services.map((service, index) => (
            <ServiceFlag key={service.id} service={service} index={index} isInView={isInView} />
          ))}
        </div>

        <motion.div
          style={{
            height: '1px', marginTop: '5rem',
            background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.08), transparent)',
            transformOrigin: 'center',
          }}
          initial={{ scaleX: 0, opacity: 0 }}
          animate={isInView ? { scaleX: 1, opacity: 1 } : { scaleX: 0, opacity: 0 }}
          transition={{ duration: 1.2, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>
    </section>
  );
};

export default ServicesSection;
